import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { resourcesData, getAllResources } from "./resourcesData";

function ResourcesPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");

  // Carousel states for each section
  const [organizeCurrentIndex, setOrganizeCurrentIndex] = useState(0);
  const [donorRelationshipsCurrentIndex, setDonorRelationshipsCurrentIndex] =
    useState(0);
  const [donorStewardshipCurrentIndex, setDonorStewardshipCurrentIndex] =
    useState(0);
  const [levelUpCurrentIndex, setLevelUpCurrentIndex] = useState(0);
  const [optimizeCurrentIndex, setOptimizeCurrentIndex] = useState(0);

  // Categories for filtering
  const categories = [
    "All Categories",
    "Checklists",
    "Emails",
    "Guides",
    "Letters",
    "Templates",
    "Worksheets",
  ];

  // Carousel navigation functions
  const handleOrganizePrevious = () => {
    setOrganizeCurrentIndex((prevIndex) =>
      prevIndex === 0 ? Math.max(0, filteredOrganize.length - 3) : prevIndex - 1
    );
  };

  const handleOrganizeNext = () => {
    setOrganizeCurrentIndex((prevIndex) =>
      prevIndex >= filteredOrganize.length - 3 ? 0 : prevIndex + 1
    );
  };

  const handleDonorRelationshipsPrevious = () => {
    setDonorRelationshipsCurrentIndex((prevIndex) =>
      prevIndex === 0
        ? Math.max(0, filteredDonorRelationships.length - 3)
        : prevIndex - 1
    );
  };

  const handleDonorRelationshipsNext = () => {
    setDonorRelationshipsCurrentIndex((prevIndex) =>
      prevIndex >= filteredDonorRelationships.length - 3 ? 0 : prevIndex + 1
    );
  };

  const handleDonorStewardshipPrevious = () => {
    setDonorStewardshipCurrentIndex((prevIndex) =>
      prevIndex === 0
        ? Math.max(0, filteredDonorStewardship.length - 3)
        : prevIndex - 1
    );
  };

  const handleDonorStewardshipNext = () => {
    setDonorStewardshipCurrentIndex((prevIndex) =>
      prevIndex >= filteredDonorStewardship.length - 3 ? 0 : prevIndex + 1
    );
  };

  const handleLevelUpPrevious = () => {
    setLevelUpCurrentIndex((prevIndex) =>
      prevIndex === 0 ? Math.max(0, filteredLevelUp.length - 3) : prevIndex - 1
    );
  };

  const handleLevelUpNext = () => {
    setLevelUpCurrentIndex((prevIndex) =>
      prevIndex >= filteredLevelUp.length - 3 ? 0 : prevIndex + 1
    );
  };

  const handleOptimizePrevious = () => {
    setOptimizeCurrentIndex((prevIndex) =>
      prevIndex === 0 ? Math.max(0, filteredOptimize.length - 3) : prevIndex - 1
    );
  };

  const handleOptimizeNext = () => {
    setOptimizeCurrentIndex((prevIndex) =>
      prevIndex >= filteredOptimize.length - 3 ? 0 : prevIndex + 1
    );
  };

  // Filter resources based on search and category
  const filterResources = (resources) => {
    return resources.filter((resource) => {
      const matchesSearch =
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All Categories" ||
        resource.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  };

  const handleDownloadAll = () => {
    const allResources = getAllResources();
    const downloadable = allResources.filter(
      (r) => r.downloadUrl && r.downloadUrl !== "#"
    );
    downloadable.forEach((resource, index) => {
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = resource.downloadUrl;
        link.download = `${resource.title}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 300);
    });
  };

  const filteredFeatured = filterResources(resourcesData.featured);
  const filteredMaximize = filterResources(resourcesData.maximize);
  const filteredOrganize = filterResources(resourcesData.organize);
  const filteredDonorRelationships = filterResources(
    resourcesData.donorRelationships
  );
  const filteredDonorStewardship = filterResources(
    resourcesData.donorStewardship
  );
  const filteredLevelUp = filterResources(resourcesData.levelUp);
  const filteredOptimize = filterResources(resourcesData.optimize);

  // Reset carousel index when filters change
  useEffect(() => {
    setOrganizeCurrentIndex(0);
    setDonorRelationshipsCurrentIndex(0);
    setDonorStewardshipCurrentIndex(0);
    setLevelUpCurrentIndex(0);
    setOptimizeCurrentIndex(0);
  }, [searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-white pt-[100px]">
      {/* Hero Section */}
      <div className="bg-blue py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="!text-[50px] text-white mb-6 font-poppins font-medium">
            Free Resources for Nonprofits
          </h1>
          <p className="!text-[22px] font-poppins font-normal text-white max-w-4xl mx-auto">
            We've created a whole collection of helpful resources to save you
            time and raise more for your cause. Explore templates, scripts,
            checklists, and how-to guides - all in one easy-to-browse place.
          </p>
          <button
            onClick={() => {
              const el = document.getElementById("download-form");
              const offset = el.getBoundingClientRect().top + window.scrollY - 100;
              window.scrollTo({ top: offset, behavior: "smooth" });
            }}
            className="mt-8 bg-orange hover:bg-orange-dark1 text-black font-semibold py-3 px-8 rounded-full transition-colors"
          >
            Download All Resources
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="!text-[40px] font-bold text-orange mb-8 text-left font-poppins">
            View Our Growing List of Free Resources
          </h2>

          <div className="flex flex-wrap gap-3 items-center justify-between mb-8">
            {/* Category Filters + Search inline */}
            <div className="flex flex-wrap gap-2 items-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors border ${
                    selectedCategory === category
                      ? "bg-blue-navy text-white border-blue-navy"
                      : "bg-white border-gray-300 text-gray-700 hover:border-blue-navy hover:text-blue-navy"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Search Bar */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-navy focus:border-blue-navy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Featured Resources Section */}
      {filteredFeatured.length > 0 && (
        <div className="bg-white py-2 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Featured Resources
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFeatured.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Maximize Results Section */}
      {filteredMaximize.length > 0 && (
        <div className="bg-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Maximize Results of Your Next Auction
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMaximize.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Organize Your Next Major Fundraising Event Section */}
      {filteredOrganize.length > 0 && (
        <div className="bg-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Organize Your Next Major Fundraising Event
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleOrganizePrevious}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleOrganizeNext}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOrganize
                .slice(organizeCurrentIndex, organizeCurrentIndex + 3)
                .map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Build Lasting Donor Relationships Section */}
      {filteredDonorRelationships.length > 0 && (
        <div className="bg-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Build Lasting Donor Relationships
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleDonorRelationshipsPrevious}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleDonorRelationshipsNext}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDonorRelationships
                .slice(
                  donorRelationshipsCurrentIndex,
                  donorRelationshipsCurrentIndex + 3
                )
                .map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Donor Stewardship & Retention Section */}
      {filteredDonorStewardship.length > 0 && (
        <div className="bg-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Donor Stewardship & Retention
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleDonorStewardshipPrevious}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleDonorStewardshipNext}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDonorStewardship
                .slice(
                  donorStewardshipCurrentIndex,
                  donorStewardshipCurrentIndex + 3
                )
                .map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Level Up Your Fundraising Campaigns Section */}
      {filteredLevelUp.length > 0 && (
        <div className="bg-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Level Up Your Fundraising Campaigns
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleLevelUpPrevious}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleLevelUpNext}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLevelUp
                .slice(levelUpCurrentIndex, levelUpCurrentIndex + 3)
                .map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Optimize Your Nonprofit's Daily Operations Section */}
      {filteredOptimize.length > 0 && (
        <div className="bg-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="!text-[30px] font-semibold text-blue font-poppins text-left">
                Optimize Your Nonprofit's Daily Operations
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleOptimizePrevious}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleOptimizeNext}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOptimize
                .slice(optimizeCurrentIndex, optimizeCurrentIndex + 3)
                .map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Call to Action Section */}
      <div id="download-form" className="bg-blue-navy py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div className="text-white text-left">
              <h2 className="!text-[40px] font-bold font-poppins mb-6 leading-tight">
                Want access to all of Sponsor a Pet's free fundraising templates?
              </h2>
              <p className="text-[16px] text-white font-poppins">
                Save time by downloading all of the free stuff for nonprofits
                Sponsor a Pet has to offer in one neat little package.
              </p>
            </div>

            {/* Right: Form */}
            <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); handleDownloadAll(); }}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-1 text-left">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full px-4 py-3 rounded-full bg-white text-gray-800 placeholder-gray-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-1 text-left">
                    Organization <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Organization"
                    className="w-full px-4 py-3 rounded-full bg-white text-gray-800 placeholder-gray-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-1 text-left">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="First Name"
                    className="w-full px-4 py-3 rounded-full bg-white text-gray-800 placeholder-gray-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-1 text-left">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    className="w-full px-4 py-3 rounded-full bg-white text-gray-800 placeholder-gray-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-1 text-left">
                  How much in ONLINE donations do you expect to raise in the next 12 months? <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select className="w-full px-4 py-3 rounded-full bg-white text-gray-800 appearance-none focus:outline-none">
                    <option value="">Select</option>
                    <option>Under $10,000</option>
                    <option>$10,000 - $50,000</option>
                    <option>$50,000 - $100,000</option>
                    <option>Over $100,000</option>
                  </select>
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div className="text-left">
                <button
                  type="submit"
                  className="bg-orange hover:bg-orange-dark1 text-black font-semibold py-3 px-10 rounded-full transition-colors"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// Resource Card Component
function ResourceCard({ resource }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
      <Link to={`/resources/${resource.id}`} className="block">
        <div className="h-56 bg-gray-100 flex items-center justify-center overflow-hidden">
          <img
            src={resource.image}
            alt={resource.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div
            className="w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center"
            style={{ display: "none" }}
          >
            <svg
              className="w-16 h-16 text-blue-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
        <div className="p-6 text-left">
          <span className="text-blue text-sm font-medium">
            {resource.category}
          </span>
          <h3 className="text-lg font-bold text-orange mt-1 mb-2 text-left">
            {resource.title}
          </h3>
          <p className="text-gray-600 text-sm mb-4 text-left">
            {resource.description}
          </p>
          <div className="flex items-center justify-start">
            <Link
              to={`/resources/${resource.id}/download`}
              onClick={(e) => e.stopPropagation()}
              className="text-black font-medium text-sm flex items-center gap-1 hover:text-gray-600"
            >
              Get the freebie
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default ResourcesPage;
